import { LegalPerson } from './legal-person.model';
import { PhysicalPerson } from './physical-person.model';
import { PersonType } from './person-type.model';
import { Category } from '../components/generics/data-printer/data-printer.component';
import { ID } from './ID.model';
import { CategoryV2 } from '../components/generics/data-printer-v2/data-printer-v2.component';
import { Representative } from './representative.model';

export interface Owner {
    id?: ID;
    codigo: string;
    activo?: boolean;
    email: string;
    personaFisica?: PhysicalPerson;
    personaJuridica?: LegalPerson;
    idOficina?: ID;
    idAgente?: ID;
    tipoPersona: PersonType; // Tipo de persona
}

export function getOwnerName(owner: Owner) {
    if (owner?.tipoPersona === 'FISICA' && owner.personaFisica?.nombre)
        return owner.personaFisica.nombre + ' ' + owner.personaFisica.apellido;
    else if (owner?.tipoPersona === 'JURIDICA' && owner.personaJuridica?.razonSocial)
        return owner.personaJuridica.razonSocial;
    else
        return '';
}

export function getOwnerPerson(owner: Owner): PhysicalPerson | LegalPerson {
    return owner.tipoPersona === 'FISICA' ? owner.personaFisica : owner.personaJuridica;
}

export function generateOwnerCategories(owner: Owner, generalSectionName: string, addressSectionName: string, representativeSectionName: string) {
    if (!owner) return [];
    const person = getOwnerPerson(owner);
    const categories: Category[] = [
        { name: generalSectionName, items: [
            { label: 'Tipo de persona', value: owner.tipoPersona },
            { label: 'Código', value: owner.codigo },
            { label: 'Teléfono', value: person.celular, type: 'phone' },
            { label: 'E-Mail', value: owner.email, type: 'email' },
        ]}, { name: addressSectionName, items: [
            { label: 'Tipo de vía', value: person.direccion?.tipoCalle },
            { label: 'Calle', value: person.direccion?.calle },
            { label: 'Número', value: person.direccion?.numero },
            { label: 'Piso', value: person.direccion?.piso },
            { label: 'Departamento', value: person.direccion?.departamento },
            { label: 'Código postal', value: person.direccion?.codigoPostal },
            { label: 'Provincia', value: person.direccion?.localidad?.provincia?.descripcion },
            { label: 'Municipio', value: person.direccion?.localidad?.municipio?.descripcion },
            { label: 'Localidad', value: person.direccion?.localidad?.descripcion },
        ]}
    ];
    if (owner.tipoPersona === 'JURIDICA') {
        categories[0].items.push(
            { label: 'Razón social', value: owner.personaJuridica?.razonSocial },
            { label: 'CUIT', value: owner.personaJuridica?.cuit, type: 'cuit' },
            { label: 'Rubro', value: owner.personaJuridica?.rubro?.descripcion },
            { label: 'Situación frente al IVA', value: owner.personaJuridica?.situacionIVA?.descripcion },
            { label: 'Tipo de sociedad', value: owner.personaJuridica?.tipoSociedad },
        );
    } else {
        categories[0].items.push(
            { label: 'Nombre', value: owner.personaFisica?.nombre },
            { label: 'Apellido', value: owner.personaFisica?.apellido },
            { label: 'DNI', value: owner.personaFisica?.dni },
            { label: 'Sexo', value: owner.personaFisica?.sexo },
            { label: 'Fecha de nacimiento', value: owner.personaFisica?.fechaNacimiento, type: 'date' },
        );
    }
    if (person.representante) {
        const category: Category = { name: representativeSectionName, items: [
            { label: 'Nombre', value: person.representante.nombre },
            { label: 'Apellido', value: person.representante.apellido },
            { label: 'Fecha de nacimiento', value: person.representante.fechaNacimiento, type: 'date' },
            { label: 'E-Mail', value: person.representante.mail, type: 'email' },
            { label: 'Teléfono', value: person.representante.celular, type: 'phone' },
            { label: 'Cargo', value: person.representante.cargo },
        ]};
        if (person.representante.cargo === 'APODERADO') {
            category.items.push(
                { label: 'Fecha del poder', value: person.representante.fechaPoder, type: 'date' },
            );
        } else {
            category.items.push(
                { label: 'Nombre del cargo', value: person.representante.nombreCargo },
                { label: 'Nro acta asamblea/estatuto', value: person.representante.nroAsambleaEstatuto },
                { label: 'Fecha del acta', value: person.representante.fechaPoder, type: 'date' },
            );
        }
        categories.push(category);
    }
    return categories;
}

export function generateOwnerCategoriesV2(owner?: Owner) {
    if (!owner) return [];

    const categories: CategoryV2[] = [];
    let representante: Representative | undefined;

    if (owner.tipoPersona === 'FISICA' && owner.personaFisica) {
        // Información general
        categories.push({
            labelWidth: '145px',
            minWidth: '380px',
            maxWidth: '450px',
            name: 'Información general',
            items: [
                { label: 'E-Mail', value: owner.email, type: 'email' },
                { label: 'Tipo de persona', value: owner.tipoPersona, type: 'text' },
                { label: 'Código', value: owner.codigo, type: 'text' },
                { label: 'Teléfono', value: owner.personaFisica.celular, type: 'phone' },
                { label: 'Nombre', value: owner.personaFisica.nombre, type: 'text' },
                { label: 'Apellido', value: owner.personaFisica.apellido, type: 'text' },
                { label: 'DNI', value: owner.personaFisica.dni, type: 'text' },
                { label: 'Pasaporte', value: owner.personaFisica.pasaporte, type: 'text' },
                { label: 'Sexo', value: owner.personaFisica.sexo, type: 'text' },
                { label: 'Fecha de nacimiento', value: owner.personaFisica.fechaNacimiento, type: 'date' },
            ]
        });
        representante = owner.personaFisica.representante;
    } else if (owner.tipoPersona === 'JURIDICA' && owner.personaJuridica) {
        // Información general
        categories.push({
            labelWidth: '145px',
            minWidth: '380px',
            maxWidth: '500px',
            name: 'Información general',
            items: [
                { label: 'E-Mail', value: owner.email, type: 'email' },
                { label: 'Tipo de persona', value: owner.tipoPersona, type: 'text' },
                { label: 'Código', value: owner.codigo, type: 'text' },
                { label: 'Teléfono', value: owner.personaJuridica.celular, type: 'phone' },
                { label: 'Razón social', value: owner.personaJuridica.razonSocial, type: 'text' },
                { label: 'CUIT', value: owner.personaJuridica.cuit, type: 'cuit' },
                { label: 'Rubro', value: owner.personaJuridica.rubro?.descripcion, type: 'text' },
                { label: 'Situación IVA', value: owner.personaJuridica.situacionIVA?.descripcion, type: 'text' },
                { label: 'Tipo de sociedad', value: owner.personaJuridica.tipoSociedad, type: 'text' },
            ]
        });
        representante = owner.personaJuridica.representante;
    }
    if ((owner.tipoPersona === 'FISICA' && owner.personaFisica?.direccion) || (owner.tipoPersona === 'JURIDICA' && owner.personaJuridica?.direccion)) {
        // Dirección
        categories.push({
            maxWidth: '500px',
            name: 'Dirección',
            items: [
                { value: owner.tipoPersona === 'FISICA' ? owner.personaFisica?.direccion : owner.personaJuridica?.direccion, type: 'address', alignment: 'center' },
            ]
        });
    }
    if (representante) {
        const categoriaRepresentante: CategoryV2 = {
            labelWidth: '190px',
            minWidth: '380px',
            maxWidth: '500px',
            name: 'Datos del representante',
            items: [
                { label: 'E-Mail', value: representante.mail, type: 'email' },
                { label: 'Nombre', value: representante.nombre, type: 'text' },
                { label: 'Apellido', value: representante.apellido, type: 'text' },
                { label: 'Fecha de nacimiento', value: representante.fechaNacimiento, type: 'date' },
                { label: 'Teléfono', value: representante.celular, type: 'phone' },
                { label: 'Cargo', value: representante.cargo, type: 'text' },
            ]
        };
        if (representante.cargo === 'APODERADO') {
            categoriaRepresentante.items.push(
                { label: 'Fecha del poder', value: representante.fechaPoder, type: 'date' },
            );
        } else {
            categoriaRepresentante.items.push(
                { label: 'Nombre del cargo', value: representante.nombreCargo },
                { label: 'Nro acta asamblea/estatuto', value: representante.nroAsambleaEstatuto },
                { label: 'Fecha del acta', value: representante.fechaPoder, type: 'date' },
            );
        }
        categories.push(categoriaRepresentante);
    }

    return categories;
}
